import type { FunnelStep } from '@rheora/enrollment-contract';

import type { FunnelStepId } from './funnelSteps';

/**
 * ── FUNNEL GATING (E-SF19 P1) — MAY THIS SESSION RENDER THIS STEP? ──────────
 *
 * The SPA had none. Typing `/plan/:slug/sign` into the address bar on a session
 * at an early step rendered the whole sign surface: the documents, the three
 * acknowledgements, the signature pad, and a green alert reading "No deposit
 * required — you have no deposit to pay", asserted to a customer whose credit had
 * never been run.
 *
 * ── IT IS A DISPLAY GATE. IT IS NOT A CONTROL. ─────────────────────────────
 * The server refuses regardless (`POST /bff/sign/accept` → 400), and it must keep
 * doing so: a client-side gate protects nobody. What this fixes is that the page
 * was TELLING the customer things that were not true, and a button that would
 * eventually fail does not un-tell them.
 *
 * ── ⚠️ THE MISTAKE THIS FILE WAS REBUILT AROUND (read before editing) ───────
 * The first version compared the requested SPA step against the server step OF
 * THE SAME NAME, and that deadlocked the funnel for every real customer at its
 * FIRST transition. The two vocabularies share four words and are not the same
 * ladder:
 *
 *   - The server reaches step `credit` when THE EMAIL OTP IS PROVEN
 *     (`funnel-step.ts`: "proving the address IS the first server-visible act of
 *     the credit step").
 *   - But the OTP field lives ON `CreditPage`.
 *
 * So proving the email required rendering `/credit`, and rendering `/credit`
 * required a proven email. `GET /bff/funnel` returns `step: "plan"` with a
 * resolved meter and `verifiedEmail: null` — the normal state of every customer
 * the moment they pick a plan — and "Continue to approval" silently did nothing.
 *
 * The rule is therefore NOT a name match, and NOT a uniform "serverStep + 1"
 * either: `plan` must unlock `credit`, but `credit` must NOT unlock `estimate`
 * (see `STEP_ENTRY_REQUIREMENT`). Each step declares the evidence it needs, and
 * that declaration is justified by what the page DOES.
 */

/**
 * The server step that ENABLES each SPA step — its entry precondition.
 *
 * ⚠️ EACH ENTRY IS A CLAIM ABOUT WHAT THE PAGE DOES, NOT ABOUT ITS NAME.
 *
 *   `plan`     ← `plan`     A resolved meter. The plan-detail page prices against
 *                           the premise, so it needs one and nothing more.
 *
 *   `credit`   ← `plan`     ⚠️ NOT `credit`. This page HOSTS the email OTP and the
 *                           credit submit — it is what PRODUCES the server's
 *                           `credit` and `estimate` rungs. Requiring its own name
 *                           is the deadlock described above. A resolved meter is
 *                           the real precondition, and it is also what
 *                           `CreditPage`'s own `candidateId` guard checks.
 *
 *   `estimate` ← `estimate` A created prospect, i.e. the credit decision has run.
 *                           NOT merely `credit` (a proven email): this step's
 *                           "Continue to sign" fires `POST /bff/quote/draft`,
 *                           which refuses without a prospect ("Complete the credit
 *                           step before creating a quote."). Admitting a
 *                           proven-email-only session would trade one dead end for
 *                           another.
 *
 *   `sign`     ← `sign`     A DRAFT quote, which is what the estimate step's
 *                           Continue creates. Without it the sign page has nothing
 *                           to accept.
 */
const STEP_ENTRY_REQUIREMENT: Readonly<Record<FunnelStepId, FunnelStep>> = {
  plan: 'plan',
  credit: 'plan',
  estimate: 'estimate',
  sign: 'sign',
};

/**
 * The furthest server step each SPA step can PRODUCE evidence for, by completing.
 *
 * This exists for ONE reason: it makes the funnel's topology assertable. A
 * per-step rule cannot catch a deadlock, because a deadlock is a property of the
 * relationship BETWEEN steps — which is exactly how the first version shipped
 * green. `funnelGating.test.ts` walks the real order and asserts every step's
 * entry requirement is satisfied by what the step before it produces.
 *
 *   `plan`     → `plan`     Choosing a plan writes nothing server-side; the meter
 *                           was already resolved. (Not a gap — the credit page's
 *                           precondition is that same meter.)
 *   `credit`   → `estimate` Proves the email (server `credit`) AND runs the credit
 *                           check, creating the prospect (server `estimate`).
 *   `estimate` → `sign`     Creates the DRAFT quote.
 *   `sign`     → `signed`   The at-accept saga completes.
 */
const STEP_PRODUCES: Readonly<Record<FunnelStepId, FunnelStep>> = {
  plan: 'plan',
  credit: 'estimate',
  estimate: 'sign',
  sign: 'signed',
};

/**
 * The server ladder, weakest first — the order `deriveFunnelStep` walks.
 *
 * Declared here rather than read off the contract enum's member order, because
 * relying on a zod enum's declaration order for a comparison rule turns a
 * reordering that looks cosmetic into a silent gating change.
 */
const SERVER_STEP_ORDER: readonly FunnelStep[] = [
  'meter',
  'plan',
  'credit',
  'estimate',
  'sign',
  'signed',
] as const;

/** Rank of a server step on the ladder; -1 when unrecognised. */
function serverRank(step: FunnelStep): number {
  return SERVER_STEP_ORDER.indexOf(step);
}

/**
 * The FURTHER of two server steps — "everything either of them can evidence".
 *
 * ── ⚠️ THE DEFECT THIS EXISTS FOR (reproduced 2026-08-26) ──────────────────
 * `FunnelProvider` seeds `serverStep` from `GET /bff/funnel` ONCE, at mount, and
 * has no setter (deliberately — no component may advance it). But the shopper
 * ADVANCES THE SERVER while the page stays mounted: pressing "Continue to sign"
 * creates the DRAFT quote, which moves the server from `estimate` to `sign`.
 *
 * Measured: `POST /bff/quote/draft` succeeded, `GET /bff/funnel` reported
 * `step: "sign"` — and the SPA sat on `/estimate` and did nothing. `EstimatePage`
 * DID call `navigate(...sign)`; `FunnelStepGate` bounced it straight back, because
 * the provider still held the `estimate` it was seeded with a minute earlier. A
 * hard reload of `/plan/:slug/sign` then worked, which is the signature of stale
 * client state rather than a server refusal. The customer's only symptom is a
 * primary button that silently does nothing.
 *
 * ⚠️ TAKING THE FURTHEST IS SAFE IN ONE DIRECTION ONLY, AND THAT IS THE POINT.
 * `funnelGateFor` is FORWARD-ONLY: it refuses a step that runs AHEAD of the
 * evidence and never refuses one behind it. A higher `serverStep` can therefore
 * only ever REMOVE a redirect — it cannot bounce anyone anywhere — so folding in a
 * fresher snapshot cannot move a shopper mid-funnel, which is the concern the
 * seed-once rule was written to protect. A LOWER one is ignored, so a flaky read
 * that briefly reports less can never revoke progress the session already proved.
 *
 * An unrecognised value ranks -1 and loses to any known step; two unknowns return
 * the first, which keeps this total rather than throwing on a contract addition.
 */
export function furthestServerStep(
  a: FunnelStep | undefined,
  b: FunnelStep | undefined,
): FunnelStep | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return serverRank(b) > serverRank(a) ? b : a;
}

/**
 * True when the session's evidence satisfies `requirement`.
 *
 * An unrecognised step cannot be ranked, so it is not judged: refusing to render
 * on a value we do not understand would bounce a customer on a contract addition,
 * and admitting it changes nothing the server will not re-check anyway.
 */
export function serverStepSatisfies(serverStep: FunnelStep, requirement: FunnelStep): boolean {
  const have = serverRank(serverStep);
  const need = serverRank(requirement);
  if (have === -1 || need === -1) return true;
  return have >= need;
}

/** The evidence a step needs before it may render. Exported for the topology test. */
export function entryRequirementFor(step: FunnelStepId): FunnelStep {
  return STEP_ENTRY_REQUIREMENT[step];
}

/** The furthest evidence a step produces by completing. Exported for the topology test. */
export function producesFor(step: FunnelStepId): FunnelStep {
  return STEP_PRODUCES[step];
}

/**
 * Where the SPA sends a shopper who has run ahead of what the server can evidence.
 *
 * `allowedStep: null` means THE FUNNEL HAS NOT STARTED — the session holds no
 * resolved meter, so there is no plan-scoped page worth showing and the landing
 * chooser is the destination. It is `null` rather than a made-up step id because
 * the landing page is NOT a funnel step: naming it one would put it in the
 * stepper and in `FUNNEL_STEP_ORDER`, where it does not belong.
 */
export interface FunnelGateRedirect {
  /** The furthest step this session may render, or null for "start over". */
  readonly allowedStep: FunnelStepId | null;
}

export interface FunnelGateInput {
  /** The step the shopper is trying to render. */
  readonly requestedStep: FunnelStepId;
  /** The furthest step `GET /bff/funnel` can evidence, or `undefined` when unknown. */
  readonly serverStep: FunnelStep | undefined;
  /** The SPA's on-screen step order (injected so the rule has no hidden import). */
  readonly order: readonly FunnelStepId[];
}

/**
 * Whether `requestedStep` may render, and where to send the shopper if not.
 *
 * ── AN UNKNOWN SERVER STEP DOES NOT GATE ───────────────────────────────────
 * `serverStep === undefined` means the rehydrate read 401'd or failed. That is
 * NOT evidence of a shopper running ahead — it is the absence of evidence either
 * way — and turning it into a redirect would bounce a legitimate customer out of
 * a funnel they were in, on a flaky network. The steps' own `candidateId` guards
 * already send a shopper with no resolved meter back to the chooser, and the
 * server refuses anything consequential regardless. So: no snapshot, no gate.
 *
 * ── FORWARD ONLY. NEVER BACKWARD. ──────────────────────────────────────────
 * Revisiting an earlier step is legitimate — every Back link in the funnel does
 * it — and re-rendering one asserts nothing untrue. Only running AHEAD of the
 * evidence is refused, and "ahead" is decided by `STEP_ENTRY_REQUIREMENT`.
 */
export function funnelGateFor(input: FunnelGateInput): FunnelGateRedirect | null {
  const { serverStep } = input;
  if (serverStep === undefined) return null;

  if (serverStepSatisfies(serverStep, entryRequirementFor(input.requestedStep))) {
    return null;
  }

  // Refused. Send them to the furthest step this session's evidence DOES satisfy,
  // scanning the on-screen order so the destination is always a real step of the
  // funnel rather than a guess.
  let allowedStep: FunnelStepId | null = null;
  for (const step of input.order) {
    if (serverStepSatisfies(serverStep, entryRequirementFor(step))) allowedStep = step;
  }
  return { allowedStep };
}
